import { useState } from 'react';
import { RunButton, InfoCard, NoResult, AiAdvisor } from './common';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { APP } from '@/constants/testIds';
import { toast } from 'sonner';

const Irrigation = ({ farm, selectedCrop, crops, year, season }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const label = crops.find(c => c.key === selectedCrop)?.label;

  const run = async () => {
    setLoading(true); setErrMsg('');
    try { setResult(await api.irrigation({ crop_key: selectedCrop, lat: farm.lat, lon: farm.lon, year, season })); }
    catch (e) {
      setErrMsg(e?.response?.data?.detail || 'Irrigation compute failed.');
      toast.error('Irrigation compute failed');
    }
    finally { setLoading(false); }
  };

  return (
    <InfoCard title={`Irrigation Scheduler · ${label}`}
              subtitle="FAO-56 Dual-Kc daily soil-water balance — splits transpiration (Kcb·Ks·ET₀) from soil evaporation (Ke·ET₀) for higher accuracy on sparse canopies and after rain / irrigation events. Uses Open-Meteo 7-day ET₀ + rainfall.">
      <RunButton testId={APP.actions.runIrrigation} onClick={run} loading={loading}>Build schedule</RunButton>
      {errMsg && (
        <div className="mt-4 p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-800">
          <b>Weather feed unavailable:</b> {errMsg}. Irrigation planning depends on Open-Meteo — please retry shortly.
        </div>
      )}
      {!result && !loading && !errMsg && <div className="mt-4"><NoResult /></div>}
      {result && (
        <div className="mt-5 space-y-4 animate-fade-up">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ['Total ETc (7d)', result.summary.total_etc_mm_7day, 'mm'],
              ['Transpiration (7d)', result.summary.total_transpiration_mm_7day, 'mm'],
              ['Soil evap. (7d)', result.summary.total_soil_evap_mm_7day, 'mm'],
              ['Effective rain (7d)', result.summary.total_effective_rain_mm_7day, 'mm'],
              ['Irrigation needed (7d)', result.summary.total_irrigation_mm_7day, 'mm'],
              ['Root-zone depletion', result.summary.current_depletion_pct_of_taw, '% of TAW'],
              ['Surface depletion', result.summary.current_surface_depl_pct_of_tew, '% of TEW'],
              ['Next irrigation', result.summary.next_irrigation_date || 'None in window', ''],
            ].map(([l, v, u]) => (
              <div key={l} className="rounded-lg bg-icrisat-green-soft/70 border border-icrisat-green/20 p-3">
                <div className="text-[10px] uppercase text-icrisat-earth">{l}</div>
                <div className="font-display text-icrisat-green">{v ?? '—'} <span className="text-xs">{u}</span></div>
              </div>
            ))}
          </div>

          <div className="text-[11px] text-muted-foreground -mt-2">
            Method: <b>{result.summary.method || 'FAO-56 Dual Kc'}</b> · ETc = (Kcb·Ks + Ke)·ET₀
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead className="text-right">Kcb</TableHead>
                <TableHead className="text-right">Ke</TableHead>
                <TableHead className="text-right">Ks</TableHead>
                <TableHead className="text-right">ETc (mm)</TableHead>
                <TableHead className="text-right hidden md:table-cell">T (mm)</TableHead>
                <TableHead className="text-right hidden md:table-cell">E (mm)</TableHead>
                <TableHead className="text-right">Rain</TableHead>
                <TableHead className="text-right hidden md:table-cell">Eff. rain</TableHead>
                <TableHead className="text-right">Depletion</TableHead>
                <TableHead>Irrigate</TableHead>
                <TableHead className="text-right">mm</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {result.schedule.map((r) => (
                <TableRow key={r.date}>
                  <TableCell>{r.date}</TableCell>
                  <TableCell className="capitalize">{r.stage}</TableCell>
                  <TableCell className="text-right">{r.kcb}</TableCell>
                  <TableCell className="text-right">{r.ke}</TableCell>
                  <TableCell className="text-right">{r.ks}</TableCell>
                  <TableCell className="text-right">{r.etc_mm}</TableCell>
                  <TableCell className="text-right hidden md:table-cell">{r.t_crop_mm}</TableCell>
                  <TableCell className="text-right hidden md:table-cell">{r.e_soil_mm}</TableCell>
                  <TableCell className="text-right">{r.precip_mm}</TableCell>
                  <TableCell className="text-right hidden md:table-cell">{r.eff_precip_mm}</TableCell>
                  <TableCell className="text-right">{r.depletion_mm}/{r.taw_mm}</TableCell>
                  <TableCell>{r.irrigate === 'YES' ? <Badge className="bg-icrisat-mustard/70 text-icrisat-green">YES</Badge> : '—'}</TableCell>
                  <TableCell className="text-right font-medium">{r.irrigation_mm > 0 ? r.irrigation_mm : ''}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <p className="text-xs text-muted-foreground">
            Sowing used: {result.sowing_date} · <b>Kcb</b> basal crop coeff.,
            <b> Ke</b> soil evaporation coeff., <b> Ks</b> transpiration stress factor (1.0 = no stress).
          </p>
          <AiAdvisor context="irrigation" payload={result} />
        </div>
      )}
    </InfoCard>
  );
};

export default Irrigation;
